import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useMemo } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Firestore,
  Timestamp,
} from 'firebase/firestore';
import { initializeFirebase } from '../firebase';
import { useAuth } from './AuthContext';
import { KidsUserStats, UserStatsContextType, SessionType, SessionResult, DAILY_GOAL_MINUTES } from '../types';

const UserStatsContext = createContext<UserStatsContextType | undefined>(undefined);

export const useUserStats = (): UserStatsContextType => {
  const context = useContext(UserStatsContext);
  if (context === undefined) {
    throw new Error('useUserStats must be used within a UserStatsProvider');
  }
  return context;
};

interface UserStatsProviderProps {
  children: ReactNode;
}

const defaultStats: KidsUserStats = {
  totalPracticeMinutes: 0,
  grammarSessions: 0,
  readingSessions: 0,
  listeningSessions: 0,
  speakingSessions: 0,
  averageGrammarScore: 0,
  averageReadingScore: 0,
  averageListeningScore: 0,
  averageSpeakingScore: 0,
  lastPracticeDate: null,
  currentStreak: 0,
  longestStreak: 0,
  dailyPracticeMinutes: 0,
  lastDailyReset: null,
  createdAt: new Date(),
  updatedAt: new Date(),
};

// Check if we need to reset daily progress (new day)
const shouldResetDaily = (lastReset: Date | null): boolean => {
  console.log('[DEBUG] shouldResetDaily called with:', lastReset);

  if (!lastReset) {
    console.log('[DEBUG] shouldResetDaily - no lastReset, returning true');
    return true;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const lastResetDate = new Date(lastReset);
  lastResetDate.setHours(0, 0, 0, 0);

  const needsReset = today.getTime() > lastResetDate.getTime();
  console.log('[DEBUG] shouldResetDaily - today:', today.toISOString());
  console.log('[DEBUG] shouldResetDaily - lastResetDate:', lastResetDate.toISOString());
  console.log('[DEBUG] shouldResetDaily - result:', needsReset);

  return needsReset;
};

export const UserStatsProvider: React.FC<UserStatsProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState<KidsUserStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [db, setDb] = useState<Firestore | null>(null);

  const sessionStartRef = useRef<Date | null>(null);
  const sessionTypeRef = useRef<SessionType | null>(null);

  // Calculate daily progress
  const { dailyProgress, dailyMinutes, goalReached } = useMemo(() => {
    console.log('[DEBUG] Calculating daily progress...');
    console.log('[DEBUG] stats:', stats);

    if (!stats) {
      console.log('[DEBUG] No stats available, returning zeros');
      return { dailyProgress: 0, dailyMinutes: 0, goalReached: false };
    }

    // Check if we need to reset (new day)
    const needsReset = shouldResetDaily(stats.lastDailyReset);
    console.log('[DEBUG] lastDailyReset:', stats.lastDailyReset);
    console.log('[DEBUG] needsReset:', needsReset);
    console.log('[DEBUG] stats.dailyPracticeMinutes:', stats.dailyPracticeMinutes);

    const minutes = needsReset ? 0 : stats.dailyPracticeMinutes;
    const progress = Math.min(100, (minutes / DAILY_GOAL_MINUTES) * 100);

    console.log('[DEBUG] Calculated minutes:', minutes);
    console.log('[DEBUG] Calculated progress:', progress);
    console.log('[DEBUG] DAILY_GOAL_MINUTES:', DAILY_GOAL_MINUTES);

    return {
      dailyProgress: progress,
      dailyMinutes: minutes,
      goalReached: minutes >= DAILY_GOAL_MINUTES,
    };
  }, [stats]);

  // Initialize Firestore
  useEffect(() => {
    const init = async () => {
      try {
        const { db: firestore } = await initializeFirebase();
        setDb(firestore);
      } catch (err) {
        console.error('Failed to initialize Firestore:', err);
        setError('Failed to initialize database');
      }
    };
    init();
  }, []);

  // Fetch user stats when user or db changes
  useEffect(() => {
    const fetchStats = async () => {
      console.log('[DEBUG] fetchStats called');
      console.log('[DEBUG] currentUser:', currentUser?.uid);
      console.log('[DEBUG] db:', !!db);

      if (!currentUser || !db) {
        console.log('[DEBUG] fetchStats - no user or db, setting stats to null');
        setStats(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const userDocRef = doc(db, 'kidsUserStats', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        console.log('[DEBUG] fetchStats - doc exists:', userDoc.exists());

        if (userDoc.exists()) {
          const data = userDoc.data();
          console.log('[DEBUG] fetchStats - raw Firestore data:', data);
          console.log('[DEBUG] fetchStats - dailyPracticeMinutes:', data.dailyPracticeMinutes);
          const fetchedStats: KidsUserStats = {
            ...data,
            lastPracticeDate: data.lastPracticeDate?.toDate() || null,
            lastDailyReset: data.lastDailyReset?.toDate() || null,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as KidsUserStats;

          // Reset daily minutes if it's a new day
          const needsReset = shouldResetDaily(fetchedStats.lastDailyReset);
          console.log('[DEBUG] fetchStats - needsReset:', needsReset);
          console.log('[DEBUG] fetchStats - lastDailyReset:', fetchedStats.lastDailyReset);

          if (needsReset) {
            console.log('[DEBUG] fetchStats - Resetting daily minutes to 0');
            fetchedStats.dailyPracticeMinutes = 0;
            fetchedStats.lastDailyReset = new Date();

            // Update in Firestore
            await updateDoc(userDocRef, {
              dailyPracticeMinutes: 0,
              lastDailyReset: Timestamp.now(),
            });
          }

          console.log('[DEBUG] fetchStats - final fetchedStats:', fetchedStats);
          setStats(fetchedStats);
        } else {
          // Create default stats for new user
          const now = new Date();
          const newStats = {
            ...defaultStats,
            lastDailyReset: Timestamp.fromDate(now),
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          };
          await setDoc(userDocRef, newStats);
          setStats({
            ...defaultStats,
            lastDailyReset: now,
            createdAt: now,
            updatedAt: now,
          });
        }
      } catch (err) {
        console.error('Error fetching user stats:', err);
        setError('Failed to load user statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [currentUser, db]);

  const startSession = (type: SessionType) => {
    console.log('[DEBUG] startSession called with type:', type);
    console.log('[DEBUG] Current stats at session start:', stats);
    sessionStartRef.current = new Date();
    sessionTypeRef.current = type;
    console.log('[DEBUG] Session started at:', sessionStartRef.current);
  };

  const endSession = async (score?: number): Promise<SessionResult | null> => {
    console.log('[DEBUG] endSession called');
    console.log('[DEBUG] sessionStartRef.current:', sessionStartRef.current);
    console.log('[DEBUG] sessionTypeRef.current:', sessionTypeRef.current);
    console.log('[DEBUG] currentUser:', currentUser?.uid);
    console.log('[DEBUG] db:', !!db);
    console.log('[DEBUG] stats:', stats);

    if (!sessionStartRef.current || !sessionTypeRef.current || !currentUser || !db || !stats) {
      console.log('[DEBUG] endSession returning null - missing required data');
      return null;
    }

    const endTime = new Date();
    const durationMs = endTime.getTime() - sessionStartRef.current.getTime();
    // Always count at least 1 minute for any practice session
    const durationMinutes = Math.max(1, Math.round(durationMs / 60000));
    console.log('[DEBUG] Session duration:', durationMs, 'ms =', durationMinutes, 'minutes');

    const sessionType = sessionTypeRef.current;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const lastPractice = stats.lastPracticeDate ? new Date(stats.lastPracticeDate) : null;
    if (lastPractice) {
      lastPractice.setHours(0, 0, 0, 0);
    }

    // Calculate streak
    let newStreak = stats.currentStreak;
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (!lastPractice) {
      newStreak = 1;
    } else if (lastPractice.getTime() === yesterday.getTime()) {
      newStreak = stats.currentStreak + 1;
    } else if (lastPractice.getTime() < yesterday.getTime()) {
      newStreak = 1;
    }

    const longestStreak = Math.max(stats.longestStreak, newStreak);

    // Calculate daily practice (reset if new day)
    const needsReset = shouldResetDaily(stats.lastDailyReset);
    const currentDailyMinutes = needsReset ? 0 : stats.dailyPracticeMinutes;
    const newDailyMinutes = currentDailyMinutes + durationMinutes;
    console.log('[DEBUG] needsReset:', needsReset);
    console.log('[DEBUG] currentDailyMinutes:', currentDailyMinutes);
    console.log('[DEBUG] durationMinutes:', durationMinutes);
    console.log('[DEBUG] newDailyMinutes:', newDailyMinutes);

    // Calculate new averages based on session type
    const updatedStats: Partial<KidsUserStats> = {
      totalPracticeMinutes: stats.totalPracticeMinutes + durationMinutes,
      lastPracticeDate: today,
      currentStreak: newStreak,
      longestStreak,
      dailyPracticeMinutes: newDailyMinutes,
      lastDailyReset: needsReset ? today : stats.lastDailyReset,
      updatedAt: new Date(),
    };

    // Update session-specific stats
    switch (sessionType) {
      case 'grammar':
        updatedStats.grammarSessions = stats.grammarSessions + 1;
        if (score !== undefined) {
          updatedStats.averageGrammarScore =
            (stats.averageGrammarScore * stats.grammarSessions + score) /
            (stats.grammarSessions + 1);
        }
        break;
      case 'reading':
        updatedStats.readingSessions = stats.readingSessions + 1;
        if (score !== undefined) {
          updatedStats.averageReadingScore =
            (stats.averageReadingScore * stats.readingSessions + score) /
            (stats.readingSessions + 1);
        }
        break;
      case 'listening':
        updatedStats.listeningSessions = stats.listeningSessions + 1;
        if (score !== undefined) {
          updatedStats.averageListeningScore =
            (stats.averageListeningScore * stats.listeningSessions + score) /
            (stats.listeningSessions + 1);
        }
        break;
      case 'speaking':
        updatedStats.speakingSessions = stats.speakingSessions + 1;
        if (score !== undefined) {
          updatedStats.averageSpeakingScore =
            (stats.averageSpeakingScore * stats.speakingSessions + score) /
            (stats.speakingSessions + 1);
        }
        break;
    }

    try {
      console.log('[DEBUG] Saving to Firestore...');
      console.log('[DEBUG] updatedStats:', updatedStats);

      const userDocRef = doc(db, 'kidsUserStats', currentUser.uid);
      await updateDoc(userDocRef, {
        ...updatedStats,
        lastPracticeDate: Timestamp.fromDate(today),
        lastDailyReset: Timestamp.fromDate(updatedStats.lastDailyReset || today),
        updatedAt: Timestamp.now(),
      });

      console.log('[DEBUG] Firestore update successful');

      setStats((prev) => {
        const newStats = prev
          ? {
              ...prev,
              ...updatedStats,
            }
          : null;
        console.log('[DEBUG] Updated local stats:', newStats);
        return newStats;
      });
    } catch (err) {
      console.error('[DEBUG] Error updating user stats:', err);
    }

    sessionStartRef.current = null;
    sessionTypeRef.current = null;

    // Return session result for the completion modal
    const newProgress = Math.min(100, (newDailyMinutes / DAILY_GOAL_MINUTES) * 100);
    return {
      sessionType,
      durationMinutes,
      score,
      dailyProgress: newProgress,
      dailyMinutes: newDailyMinutes,
      goalReached: newDailyMinutes >= DAILY_GOAL_MINUTES,
    };
  };

  const refreshStats = async () => {
    console.log('[DEBUG] refreshStats called');
    console.log('[DEBUG] currentUser:', currentUser?.uid);
    console.log('[DEBUG] db:', !!db);

    if (!currentUser || !db) {
      console.log('[DEBUG] refreshStats returning early - no user or db');
      return;
    }

    setLoading(true);
    try {
      const userDocRef = doc(db, 'kidsUserStats', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      console.log('[DEBUG] refreshStats - doc exists:', userDoc.exists());

      if (userDoc.exists()) {
        const data = userDoc.data();
        console.log('[DEBUG] refreshStats - raw data from Firestore:', data);
        console.log('[DEBUG] refreshStats - dailyPracticeMinutes from Firestore:', data.dailyPracticeMinutes);

        const refreshedStats = {
          ...data,
          lastPracticeDate: data.lastPracticeDate?.toDate() || null,
          lastDailyReset: data.lastDailyReset?.toDate() || null,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as KidsUserStats;

        console.log('[DEBUG] refreshStats - processed stats:', refreshedStats);
        setStats(refreshedStats);
      }
    } catch (err) {
      console.error('[DEBUG] Error refreshing stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const value: UserStatsContextType = {
    stats,
    loading,
    error,
    dailyProgress,
    dailyMinutes,
    goalReached,
    startSession,
    endSession,
    refreshStats,
  };

  return (
    <UserStatsContext.Provider value={value}>
      {children}
    </UserStatsContext.Provider>
  );
};
